import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismCard,
  PrismLoadingBar,
  PrismToggle,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { or } from 'ember-truth-helpers';
import type HighlightConfig from 'lumen/models/roi-trophies/highlight-config';
import type AjaxService from 'lumen/services/ajax';

interface SettingsPersonalFormsFormOtherSignature {
  Args: {
    highlightConfig?: HighlightConfig | null;
  };
}

export default class SettingsPersonalFormsFormOther extends Component<SettingsPersonalFormsFormOtherSignature> {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked isCsvExport = false;

  toggleCsvExportTask = task({ drop: true }, async (): Promise<void> => {
    this.isCsvExport = !this.isCsvExport;

    try {
      const URL = `/users/${this.hotelAndMarketService.user?.id}/`;
      const data = {
        user: {
          email: this.hotelAndMarketService.user.email,
          receive_csv_reports: this.isCsvExport,
        },
      };

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const payload = await this.ajax.patch(URL, {
        data: JSON.stringify(data),
        contentType: 'application/json',
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload(payload);

      this.toast.success(this.intl.t('settings.personal.update_success'), {
        showIcon: true,
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      this.errorTracking.addBreadcrumb({
        message: `There was an issue updating the 'other' personal settings for user: ${this.hotelAndMarketService.user?.id}`,
        data: { error },
      });
      void this.alerting.showErrorAlert({
        title: this.intl.t('user.update_error'),
      });
    }

    this.isCsvExport = this.hotelAndMarketService.user.receiveCsvReports;
  });

  toggleWeeklyRecapTask = task({ drop: true }, async (): Promise<void> => {
    const config = this.args.highlightConfig;

    if (!config) {
      return;
    }

    config.emailEnabled = !config.emailEnabled;

    try {
      await config.save({ adapterOptions: { usePatch: true } });

      this.toast.success(this.intl.t('settings.personal.update_success'), {
        showIcon: true,
      });
    } catch (error) {
      this.errorTracking.captureException(error);
      config.rollbackAttributes();
      void this.alerting.showErrorAlert({
        title: this.intl.t('user.update_error'),
      });
    }
  });

  // Disabled when the highlight config failed to load (or is absent): the
  // setting stays visible but can't be toggled without its backing state.
  get isWeeklyRecapDisabled(): boolean {
    return !this.args.highlightConfig;
  }

  get isWeeklyRecapEnabled(): boolean {
    return this.args.highlightConfig?.emailEnabled ?? false;
  }

  constructor(
    owner: Owner,
    args: SettingsPersonalFormsFormOtherSignature['Args'],
  ) {
    super(owner, args);

    this.isCsvExport = this.hotelAndMarketService.user.receiveCsvReports;
  }

  <template>
    {{#if
      (or
        this.toggleCsvExportTask.isRunning this.toggleWeeklyRecapTask.isRunning
      )
    }}
      <PrismLoadingBar />
    {{/if}}

    <PrismCard @contentLayout="noPadding" data-test-id="form-other">
      <:content>
        <div class="flex flex-col gap-400 p-400">
          <div class="flex flex-col gap-100">
            <PrismToggle
              @dataTestId="toggle-csv-export"
              @isChecked={{this.isCsvExport}}
              @label={{t "settings.account.csv_exports.title"}}
              @onChange={{this.toggleCsvExportTask.perform}}
            />
            <p class="paragraph-300--regular text-neutral-subdued">
              {{t "settings.account.csv_exports.description"}}
            </p>
          </div>

          {{#if this.access.hasTrophyHighlights}}
            <div class="flex flex-col gap-100">
              <PrismToggle
                @dataTestId="toggle-weekly-performance-recap-mail-subscription"
                @isChecked={{this.isWeeklyRecapEnabled}}
                @isDisabled={{this.isWeeklyRecapDisabled}}
                @label={{t
                  "settings.account.weekly_performance_recap_mails.title"
                }}
                @onChange={{this.toggleWeeklyRecapTask.perform}}
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
